import { AxiosInstance, AxiosResponse } from "axios"
import { useApiCommand } from "hooks/core/useApiCommand"
import { pollAsync, fetchAsyncResult } from "api/Engine/data-reporting"

// Define the return type of the hook
interface UseAsyncReturn {
  pollingAsync: (asyncID: string) => Promise<AxiosResponse<unknown>>
  isPollingAsync: boolean
  getAsyncResult: (asyncID: string) => Promise<AxiosResponse<unknown>>
  isFetchingAsyncResult: boolean
}

/**
 * TEXT ON THE WIRE, OBJECT AT THIS CALL SITE — the contract, stated once.
 *
 * `api/asyncJob.fetchAsyncResult` asks for `responseType: "text"` deliberately: the servers
 * answer `Content-Type: text/plain`, and axios' default transform would JSON-parse any body that
 * happens to look like JSON. That is intolerable for the `resolveAsync*` family, whose payloads
 * include base64 files and whose own `String(data)` would otherwise receive "[object Object]".
 * So the transport hands back RAW TEXT and each consumer family says what it wants.
 *
 * This hook is the other family. Its three call sites — the Query Console's parked query, the
 * reporting data loader and the AI conversion modal — read `response.data` as a parsed object
 * (`as EngineQueryResult`, `as AIConversionResponse`). They are `as` casts, so neither the
 * compiler nor a test would catch a string arriving there: the symptoms would be a query that
 * renders nothing, "Invalid async result format", and "Conversion completed with errors" — three
 * different-looking bugs with one cause.
 *
 * So the parse happens HERE, once, rather than at each of those three sites: `getAsyncResult`
 * wraps `fetchAsyncResult` and re-parses its text body. A body that is already an object is
 * passed through untouched (nothing double-handles it), and a body that cannot be read fails
 * loudly with the job id in the message instead of arriving as a string wearing an object's type.
 */
const parseAsyncResultBody = (data: unknown, asyncID: string): unknown => {
  // Already structured — an axios instance or interceptor parsed it. Leave it exactly as it is.
  if (typeof data !== "string") return data
  try {
    return JSON.parse(data)
  } catch {
    throw new Error(
      `The result of job ${asyncID} came back in a form this app could not read.`
    )
  }
}

/**
 * `fetchAsyncResult`, with its text body read back into the object shape this hook's callers
 * expect. Declared at module scope so `useApiCommand` receives a stable function (it is the
 * mutationFn's payload, and a new identity every render would be churn for nothing).
 */
const fetchParsedAsyncResult = async (
  axiosInstance: AxiosInstance,
  asyncID: string
): Promise<AxiosResponse<unknown>> => {
  const response = await fetchAsyncResult(axiosInstance, asyncID)
  return { ...response, data: parseAsyncResultBody(response.data, asyncID) }
}

/**
 * Custom hook for managing asynchronous polling and result fetching API calls for data reporting.
 *
 * @returns {UseAsyncReturn} - An object containing functions for polling and fetching asynchronous results, along with their loading states.
 *
 * @example
 * const {
 *   pollingAsync,
 *   isPollingAsync,
 *   getAsyncResult,
 *   isFetchingAsyncResult,
 * } = useAsync();
 */
export const useAsync = (): UseAsyncReturn => {
  const [pollingAsync, isPollingAsync] = useApiCommand(pollAsync)
  const [getAsyncResult, isFetchingAsyncResult] = useApiCommand(
    fetchParsedAsyncResult
  )

  return {
    /**
     * Polls the server to check if an asynchronous request is ready.
     * @param asyncID - The identifier for the asynchronous request to check readiness for.
     * @returns Promise resolving to the API response
     */
    pollingAsync,

    /**
     * Indicates whether an async polling request is currently in progress.
     */
    isPollingAsync,

    /**
     * Fetches the result of a completed asynchronous request.
     *
     * `data` is the PARSED result — see the contract note above. The transport carries text; the
     * three call sites cast this to their own result shape, and that cast is only honest because
     * the parse happens here.
     *
     * @param asyncID - The identifier for the asynchronous request whose result is being fetched.
     * @returns Promise resolving to the API response containing the result data
     */
    getAsyncResult,

    /**
     * Indicates whether an async result fetch is currently in progress.
     */
    isFetchingAsyncResult,
  }
}
